import { ClientEntity } from 'src/app/models/clients';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BaseHttpService } from './base.http.service';
import { Injectable } from '@angular/core';
import {
  ICashflowAvailableDataResult,
  ICashflowAvailableData,
  ICashflowCouponsResponse,
  ICashflowRequest,
} from './../../models/cashflow';
import { CachedHttpObservableService } from './cached.http-observable.service';
import { AppConfig } from 'src/app/app.config';
import { IPortfolio } from '../../models/portfolios';
import { catchError, map, startWith } from 'rxjs/operators';
import { SnackBarService } from '../snackbar/snackBar.service';
import { statuses } from '../utils/enums';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';
import {deduplicatingSwitchMap} from "../../utils/deduplicatingSwitchMap";

@Injectable()
export class CashflowService extends BaseHttpService {
  private _cashflowCouponsResonse$: BehaviorSubject<ICashflowCouponsResponse> =
    new BehaviorSubject<ICashflowCouponsResponse>({
      loading: false,
      data: [],
      error: false,
    });
  public cashflowCouponsResponse$: Observable<ICashflowCouponsResponse> =
    this._cashflowCouponsResonse$.asObservable();

  private _cashflowAvailableResponse$: BehaviorSubject<ICashflowAvailableDataResult> =
    new BehaviorSubject<ICashflowAvailableDataResult>({
      cashflowAvailableData: null,
      date: null,
      clientEntities: [],
      portfolios: [],
      loading: false,
      error: false,
      isLatest: false
    });

  public cashflowAvailableResponse$: Observable<ICashflowAvailableDataResult> =
    this._cashflowAvailableResponse$.asObservable();

  constructor(
    private http: HttpClient,
    private cachedHttpObservableService: CachedHttpObservableService,
    private appConfig: AppConfig,
    private snackBarService: SnackBarService
  ) {
    super();
  }
  get envSettings(): IEnvironmentConfig {
    return this.appConfig.getEnvironmentConfig();
  }

  public getCashAvailableData(paramsObject: ICashflowRequest): void {

    this._cashflowAvailableResponse$.next({
      cashflowAvailableData: null,
      date: null,
      clientEntities: [],
      portfolios: [],
      error: false,
      loading: true,
      isLatest: false
    });

    var url = this.generateCashflowAvailableURL(paramsObject)

    this.cachedHttpObservableService
      .getCachedHttpGetObservable(url.toString(), this.http)
      .pipe(
        deduplicatingSwitchMap((data: ICashflowAvailableData) => {
          return of({
            cashflowAvailableData: data,
            date: paramsObject.date,
            clientEntities: paramsObject.clientEntities,
            portfolios: paramsObject.portfolios,
            error: false,
            loading: false,
            isLatest: paramsObject.isLatest
          });
        }),
        catchError(() => {
          this.snackBarService.showWarningSnackBar("Cashflow available data couldn't be loaded.");
          return of({
            cashflowAvailableData: null,
            date: null,
            clientEntities: [],
            portfolios: [],
            error: true,
            loading: false,
            isLatest: false
          });
        }))
      .subscribe(response => this._cashflowAvailableResponse$.next(response));;
  }

  public getCashflowCouponsData(paramsObject: ICashflowRequest): void {
    this._cashflowCouponsResonse$.next({
      loading: true,
      data: [],
      error: false,
    });

    const url = this.generateCashflowCouponsURL(paramsObject);

    this.cachedHttpObservableService
      .getCachedHttpGetObservable(url.toString(), this.http)
      .pipe(
        map((data: any[]) => {
          return {
            loading: false,
            data,
            error: false,
          };
        }),
        catchError(() => {
          this.snackBarService.showWarningSnackBar("Cashflow coupons data couldn't be loaded.");
          return of({
            loading: false,
            data: [],
            error: true,
          });
        })
      )
      .subscribe(response => this._cashflowCouponsResonse$.next(response));
  }

  private generateCashflowCouponsURL(paramsObject: ICashflowRequest): URL {
    const url = new URL(
      `${this.envSettings.webApiUrl}portfolios/cashflow/upcoming`
    );

    const date =
      paramsObject.date !== undefined
        ? this.parseDate(paramsObject.date)
        : this.parseDate(new Date());
    url.searchParams.set('date', date.toString());

    const spanMonths =
      paramsObject.spanMonths !== undefined ? paramsObject.spanMonths : '';
    url.searchParams.set('spanMonths', spanMonths.toString());

    const entityIds = paramsObject.clientEntities.map(
      (selectedEntity: ClientEntity) => selectedEntity.EntityId
    );
    entityIds.forEach(item => {
      url.searchParams.append('entities', item.toString());
    });

    if (paramsObject.portfolios && paramsObject.portfolios.length > 0) {
      const portfolioIds = paramsObject.portfolios.map(
        (selectedPortfolio: IPortfolio) => selectedPortfolio.id
      );
      portfolioIds.forEach(item => {
        url.searchParams.append('portfolios', item.toString());
      });
    }

    return url;
  }

  private generateCashflowAvailableURL(paramsObject: ICashflowRequest): URL {
    const url = new URL(
      `${this.envSettings.webApiUrl}portfolios/cashAvailable-viewonly`
    );

    const date =
      paramsObject.date !== undefined
        ? this.parseDate(paramsObject.date)
        : this.parseDate(new Date());
    url.searchParams.set('date', date.toString());

    const entityIds = paramsObject.clientEntities.map(
      (selectedEntity: ClientEntity) => selectedEntity.EntityId
    );
    entityIds.forEach(item => {
      url.searchParams.append('entities', item.toString());
    });

    if (paramsObject.portfolios && paramsObject.portfolios.length > 0) {
      const portfolioIds = paramsObject.portfolios.map(
        (selectedPortfolio: IPortfolio) => selectedPortfolio.id
      );
      portfolioIds.forEach(item => {
        url.searchParams.append('portfolios', item.toString());
      });
    }

    return url;
  }
}
